import { useEffect, useMemo, useState } from "react";
import { Plane, Clock, Sunrise, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeLiveWait } from "@/lib/api";

/**
 * Gatwick "live" wait estimator.
 *
 * Note: Gatwick Airport does not publish a public real-time wait-time API.
 * This component surfaces a time-of-day heuristic based on published typical
 * peak patterns for LGW North and South Terminals.
 *
 *  - Now mode: refreshes every 60s, shows minute counts for both terminals.
 *  - Tomorrow mode: sweeps 24h and highlights the quietest 3-hour window.
 */

// base minute values for a given hour (0-23) — same shape for both terminals,
// slightly different amplitudes per terminal
const hourlyLevel = (hour) => {
  // three humps: early-morning long-haul, mid-morning, late-afternoon
  const peaks = [
    { h: 5, w: 20 },
    { h: 9, w: 15 },
    { h: 16, w: 18 },
    { h: 20, w: 10 },
  ];
  const base = 4; // quietest floor
  let val = base;
  for (const p of peaks) {
    const d = Math.min(Math.abs(hour - p.h), 24 - Math.abs(hour - p.h));
    val = Math.max(val, base + p.w * Math.exp(-(d * d) / 6));
  }
  return val;
};

const computeWait = (now, terminal = "north") => {
  const hour = now.getHours() + now.getMinutes() / 60;
  const day = now.getDay(); // 0 Sun, 6 Sat
  const level = hourlyLevel(hour);
  const weekend = day === 0 || day === 5 || day === 6 ? 1.15 : 1.0;
  const terminalMod = terminal === "south" ? 1.08 : 1.0;
  const wobble = ((now.getMinutes() * 7 + (terminal === "south" ? 3 : 0)) % 5) - 2;
  const minutes = Math.max(3, Math.round(level * weekend * terminalMod + wobble));
  let status = "quiet";
  if (minutes >= 24) status = "busy";
  else if (minutes >= 14) status = "moderate";
  return { minutes, status };
};

const statusColors = {
  quiet: { dot: "bg-emerald-500", text: "text-emerald-400", label: "Quiet" },
  moderate: { dot: "bg-amber-400", text: "text-amber-300", label: "Moderate" },
  busy: { dot: "bg-rose-500", text: "text-rose-400", label: "Busy" },
};

// Sweep 24 hours of tomorrow, return per-hour combined score + quietest window
const computeTomorrow = (baseDate) => {
  const start = new Date(baseDate);
  start.setDate(start.getDate() + 1);
  start.setHours(0, 0, 0, 0);
  const hours = [];
  for (let h = 0; h < 24; h += 1) {
    const d = new Date(start);
    d.setHours(h);
    const n = computeWait(d, "north");
    const s = computeWait(d, "south");
    // average of the two terminals for a single sweepable metric
    hours.push({ hour: h, minutes: Math.round((n.minutes + s.minutes) / 2) });
  }
  // find quietest 3-hour rolling window (avg minutes)
  let bestStart = 0;
  let bestAvg = Infinity;
  for (let i = 0; i < 22; i += 1) {
    const avg = (hours[i].minutes + hours[i + 1].minutes + hours[i + 2].minutes) / 3;
    if (avg < bestAvg) { bestAvg = avg; bestStart = i; }
  }
  return { start, hours, bestStart, bestAvg: Math.round(bestAvg) };
};

const fmtHour = (h) => {
  const s = h % 12 === 0 ? 12 : h % 12;
  return `${s}${h < 12 ? "am" : "pm"}`;
};

export const GatwickLiveWait = ({ variant = "dark", area }) => {
  const [now, setNow] = useState(() => new Date());
  const [mode, setMode] = useState("now"); // "now" | "tomorrow"
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (mode !== "now") return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [mode]);

  const north = useMemo(() => computeWait(now, "north"), [now]);
  const south = useMemo(() => computeWait(now, "south"), [now]);
  const tomorrow = useMemo(() => computeTomorrow(now), [now]);
  const updated = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const isDark = variant === "dark";
  const container = isDark
    ? "bg-white/[0.04] border border-white/10 text-white"
    : "bg-white border border-[#EAEBEA] text-ink";
  const subText = isDark ? "text-white/50" : "text-ink/50";
  const cardBg = isDark ? "bg-white/[0.04] border border-white/10" : "bg-surface border border-[#EAEBEA]";
  const toggleBg = isDark ? "bg-white/5 border border-white/10" : "bg-surface border border-[#EAEBEA]";
  const toggleActive = isDark ? "bg-brand text-white" : "bg-ink text-white";
  const toggleInactive = isDark ? "text-white/60 hover:text-white" : "text-ink/60 hover:text-ink";

  // tomorrow chart values
  const maxMin = Math.max(...tomorrow.hours.map((h) => h.minutes));
  const quietestLabel = `${fmtHour(tomorrow.bestStart)}–${fmtHour((tomorrow.bestStart + 3) % 24)}`;
  const tomorrowDate = tomorrow.start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await subscribeLiveWait({
        email,
        tomorrow_date: tomorrowDate,
        quietest_window: quietestLabel,
        quietest_avg: tomorrow.bestAvg,
        area: area || undefined,
      });
      setSubscribed(true);
      toast.success("Emailed you tomorrow's quietest window.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="gatwick-live-wait" className={`rounded-2xl p-5 ${container}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`${mode === "now" ? "animate-ping absolute" : ""} inline-flex h-full w-full rounded-full bg-brand opacity-75`} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand" />
          </span>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-brand-light" : "text-brand"}`}>
            {mode === "now" ? "Live estimate · Gatwick" : `Tomorrow · ${tomorrowDate}`}
          </p>
        </div>
        <p className={`text-[10px] uppercase tracking-widest ${subText}`}>
          {mode === "now" ? `Updated ${updated}` : "24h forecast"}
        </p>
      </div>

      {/* toggle */}
      <div className={`inline-flex items-center gap-0.5 rounded-full p-0.5 mb-4 ${toggleBg}`}>
        <button
          type="button"
          data-testid="live-wait-tab-now"
          onClick={() => setMode("now")}
          className={`text-[11px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 transition-colors ${mode === "now" ? toggleActive : toggleInactive}`}
        >
          Now
        </button>
        <button
          type="button"
          data-testid="live-wait-tab-tomorrow"
          onClick={() => setMode("tomorrow")}
          className={`text-[11px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 ${mode === "tomorrow" ? toggleActive : toggleInactive}`}
        >
          <Sunrise className="h-3 w-3" /> Tomorrow
        </button>
      </div>

      {mode === "now" ? (
        <div className="grid grid-cols-2 gap-3" data-testid="live-wait-now-view">
          {[{ label: "North Terminal", data: north, tid: "wait-north" }, { label: "South Terminal", data: south, tid: "wait-south" }].map((t) => {
            const c = statusColors[t.data.status];
            return (
              <div key={t.label} data-testid={t.tid} className={`rounded-xl p-3 ${cardBg}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Plane className={`h-3.5 w-3.5 ${isDark ? "text-brand-light" : "text-brand"}`} strokeWidth={2} />
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${subText}`}>{t.label}</p>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className={`font-heading text-2xl font-black ${isDark ? "text-white" : "text-ink"}`}>{t.data.minutes}</span>
                  <span className={`text-xs ${subText}`}>min</span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${c.text}`}>{c.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="live-wait-tomorrow-view">
          <div className={`rounded-xl p-3 mb-3 ${cardBg}`}>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${subText}`}>Quietest window</p>
            <p className={`font-heading text-2xl font-black mt-0.5 ${isDark ? "text-white" : "text-ink"}`} data-testid="live-wait-quietest">{quietestLabel}</p>
            <p className={`text-[11px] mt-0.5 ${subText}`}>~{tomorrow.bestAvg} min average · perfect for a stress-free transfer</p>
          </div>

          {/* mini 24h bar chart */}
          <div className="flex items-end gap-[3px] h-16 mb-2" role="img" aria-label="24-hour wait forecast">
            {tomorrow.hours.map((h) => {
              const height = Math.max(6, (h.minutes / maxMin) * 100);
              const isQuiet = h.hour >= tomorrow.bestStart && h.hour < tomorrow.bestStart + 3;
              const barColor = isQuiet
                ? "bg-brand"
                : isDark ? "bg-white/25" : "bg-ink/20";
              return (
                <div
                  key={h.hour}
                  data-testid={`live-wait-hour-${h.hour}`}
                  title={`${fmtHour(h.hour)} · ${h.minutes} min`}
                  className="flex-1 flex flex-col items-center justify-end"
                >
                  <div className={`w-full rounded-t-sm transition-colors ${barColor}`} style={{ height: `${height}%` }} />
                </div>
              );
            })}
          </div>
          <div className={`flex justify-between text-[9px] uppercase tracking-widest font-semibold ${subText}`}>
            <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
          </div>

          {/* email subscribe */}
          <div className={`mt-4 pt-4 border-t ${isDark ? "border-white/10" : "border-[#EAEBEA]"}`}>
            {subscribed ? (
              <div data-testid="live-wait-subscribed" className="flex items-center gap-2 text-[12px]">
                <CheckCircle2 className={`h-4 w-4 ${isDark ? "text-brand-light" : "text-brand"}`} strokeWidth={2.25} />
                <p className={isDark ? "text-white/80" : "text-ink/70"}>
                  Sent. Check your inbox for tomorrow's quietest window.
                </p>
              </div>
            ) : (
              <>
                <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5 ${isDark ? "text-brand-light" : "text-brand"}`}>
                  <Mail className="h-3 w-3" /> Email me tomorrow's quietest window
                </p>
                <form onSubmit={onSubscribe} data-testid="live-wait-subscribe-form" className="flex gap-2">
                  <input
                    type="email"
                    required
                    disabled={submitting}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="live-wait-subscribe-email"
                    placeholder="you@company.com"
                    className={`flex-1 min-w-0 rounded-full text-sm px-4 py-2 outline-none transition-colors ${
                      isDark
                        ? "bg-white/5 border border-white/15 text-white placeholder:text-white/35 focus:border-brand-light"
                        : "bg-surface border border-transparent text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand"
                    }`}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="live-wait-subscribe-btn"
                    className="shrink-0 rounded-full bg-brand hover:bg-brand-light text-white text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors disabled:opacity-70 inline-flex items-center gap-1.5"
                  >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <p className={`mt-4 text-[11px] leading-relaxed flex items-start gap-1.5 ${subText}`}>
        <Clock className="h-3 w-3 mt-0.5 shrink-0" />
        Typical terminal wait for private-hire drop-off, based on time-of-day patterns.
        Your Clocktower driver plans around it.
      </p>
    </div>
  );
};
